import { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight, LogOut, Menu, Moon, PlusCircle, Sparkles, Sun, Wrench } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { useAuth } from "@/context/AuthContext";
import { NAV, ROLE_LABELS, FRONT_OFFICE } from "@/lib/i18n";
import { GlobalSearch } from "@/components/GlobalSearch";
import { AiAssistant } from "@/components/AiAssistant";
import { cn } from "@/lib/utils";

function NavList({ collapsed, onNavigate }) {
  const { user } = useAuth();
  const items = NAV.filter((n) => n.roles.includes(user.role));
  return (
    <nav className="flex flex-1 flex-col gap-1 px-3" data-testid="sidebar-nav">
      {items.map((n) => (
        <NavLink key={n.id} to={n.path} onClick={onNavigate} data-testid={`nav-${n.id}`} className={({ isActive }) => cn("group flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium text-slate-300 transition-[background-color,color] hover:bg-white/5 hover:text-white", isActive && "bg-primary/15 text-white shadow-[inset_3px_0_0_0_hsl(var(--primary))]")} title={n.label}>
          <n.icon className="h-[18px] w-[18px] shrink-0" />
          {!collapsed && <span className="truncate">{n.label}</span>}
        </NavLink>
      ))}
    </nav>
  );
}

function Brand({ collapsed }) {
  return (
    <div className={cn("flex items-center gap-3 px-5 py-5", collapsed && "justify-center px-0")}>
      <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-primary text-white shadow-[0_8px_24px_-8px_rgb(var(--glow)/0.9)]"><Wrench className="h-5 w-5" /></span>
      {!collapsed && (
        <div className="leading-tight">
          <div className="font-heading text-base font-extrabold tracking-tight text-white">Concept By Yk</div>
          <div className="eyebrow !text-slate-400">CRM Atelier</div>
        </div>
      )}
    </div>
  );
}

export default function AppShell() {
  const { user, logout, theme, toggleTheme } = useAuth();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const navigate = useNavigate();
  const isStaff = user.role !== "client";

  return (
    <div className="flex min-h-screen bg-background">
      <aside className={cn("hidden shrink-0 flex-col border-r border-white/5 bg-[hsl(var(--sidebar))] transition-[width] duration-200 lg:flex", collapsed ? "w-[76px]" : "w-[260px]")} data-testid="sidebar">
        <Brand collapsed={collapsed} />
        <NavList collapsed={collapsed} />
        <div className="mt-auto flex flex-col gap-1 p-3">
          <button onClick={() => setCollapsed((c) => !c)} data-testid="sidebar-collapse-button" className="flex items-center justify-center gap-2 rounded-xl px-3 py-2 text-xs text-slate-400 hover:bg-white/5 hover:text-white">
            {collapsed ? <ChevronRight className="h-4 w-4" /> : <><ChevronLeft className="h-4 w-4" /> Réduire</>}
          </button>
        </div>
      </aside>

      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent side="left" className="w-[280px] border-white/5 bg-[hsl(var(--sidebar))] p-0" data-testid="mobile-sidebar">
          <Brand />
          <NavList onNavigate={() => setMobileOpen(false)} />
        </SheetContent>
      </Sheet>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-40 flex h-16 items-center gap-3 border-b border-border/70 bg-background/80 px-4 backdrop-blur-xl sm:px-6" data-testid="topbar">
          <Button variant="ghost" size="icon" className="lg:hidden" onClick={() => setMobileOpen(true)} data-testid="mobile-menu-button"><Menu className="h-5 w-5" /></Button>
          {isStaff ? <GlobalSearch /> : <div className="flex-1 font-heading font-bold">Espace client · Concept By Yk</div>}
          <div className="ml-auto flex items-center gap-1.5 sm:gap-2">
            {FRONT_OFFICE.includes(user.role) && (
              <Button onClick={() => navigate("/atelier/nouveau")} className="hidden h-9 rounded-full sm:inline-flex" data-testid="quick-reception-button"><PlusCircle className="mr-1.5 h-4 w-4" /> Réception</Button>
            )}
            {isStaff && (
              <Button variant="outline" onClick={() => setAiOpen(true)} className="h-9 rounded-full border-primary/40 text-primary hover:bg-primary/10" data-testid="ai-assistant-drawer-trigger"><Sparkles className="h-4 w-4 sm:mr-1.5" /><span className="hidden sm:inline">Assistant IA</span></Button>
            )}
            <Button variant="ghost" size="icon" onClick={toggleTheme} data-testid="theme-toggle-button" title="Basculer le thème">{theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}</Button>
            <div className="hidden items-center gap-2 rounded-full border border-border bg-secondary/50 py-1 pl-1 pr-3 md:flex" data-testid="user-chip">
              <span className="grid h-7 w-7 place-items-center rounded-full bg-primary/20 text-xs font-bold text-primary">{user.name?.[0]}</span>
              <div className="leading-tight">
                <div className="text-xs font-semibold">{user.name}</div>
                <div className="text-[10px] text-muted-foreground">{ROLE_LABELS[user.role]}</div>
              </div>
            </div>
            <Button variant="ghost" size="icon" onClick={() => logout().then(() => navigate("/connexion"))} data-testid="logout-button" title="Se déconnecter"><LogOut className="h-4 w-4" /></Button>
          </div>
        </header>
        <main className="flex-1 p-4 sm:p-6 lg:p-8" data-testid="main-content">
          <Outlet />
        </main>
      </div>
      {isStaff && <AiAssistant open={aiOpen} onOpenChange={setAiOpen} />}
    </div>
  );
}
